import * as d3 from "d3"
import { capitalize } from "../../../helpers/string_helpers"

export default class AxisDrawer {
    static drawAxises(args) {
        return new AxisDrawer(args).draw()
    }

    constructor({ chartController, configuration, series, visibleSeries = series, interactive = true }) {
        this.interactive = interactive
        this.chartController = chartController
        this.chartTarget = chartController.chartTarget
        this.chartSize = chartController.chartSize
        this.layersAboveSize = chartController.layersAboveSize || 0
        this.configuration = configuration
        this.series = series
        this.visibleSeries = visibleSeries
    }

    draw() {
        if (this.interactive) this.renderAxisSelects()
        this.setAxisConfigs()
        this.setDomains()
        this.setMargins()
        this.setScales()
        this.drawGrids()
        this.drawXAxis()
        this.drawYAxis()
        if (this.secondarySeries.length) this.drawSecondaryYAxis()
        this.drawAxisLabels()

        return {
            xScale: this.xScale,
            yScale: this.yScale,
            secondaryYScale: this.secondaryYScale,
            xAxisScaleType: this.xAxis.scaleTypeName,
            yAxisScaleType: this.yAxis.scaleTypeName,
            xFormatter: this.axisFormatter(this.xAxis, this.xScale),
            yFormatter: this.axisFormatter(this.yAxis, this.yScale),
            xValueFormatter: this.valueFormatter(this.xAxis, this.xScale),
            yValueFormatter: this.valueFormatter(this.yAxis, this.yScale),
            secondaryYValueFormatter: this.secondaryYScale ? this.valueFormatter(this.yAxis, this.secondaryYScale) : null,
            margin: this.margin,
            innerWidth: this.innerWidth,
            innerHeight: this.innerHeight
        }
    }

    renderAxisSelects() {
        this.renderAxisSelect("x")
        this.renderAxisSelect("y")
    }

    renderAxisSelect(axis) {
        const container = this.chartController[`${axis}AxisOptionsContainerTarget`]
        const currentValue = this.chartController[`${axis}AxisValue`]
        const select = d3.create("select")
            .attr("data-controller", "form-select")
            .attr("data-show-search", "false")
            .attr(`data-${this.chartController.identifier}-target`, `${axis}AxisOptions`)
            .on("change", event => {
                this.chartController[`${axis}AxisValue`] = event.currentTarget.value
                this.chartController.draw()
            })

        select
            .selectAll("option")
            .data(this.configuration.axisConfigs(axis))
            .join("option")
            .attr("value", axisConfig => axisConfig.label)
            .property("selected", axisConfig => axisConfig.label === currentValue)
            .text(axisConfig => capitalize(axisConfig.label))

        container.replaceChildren(select.node())
    }

    setAxisConfigs() {
        this.xAxis = this.configuration.axisByLabel("x", this.chartController.xAxisValue)
        this.yAxis = this.configuration.axisByLabel("y", this.chartController.yAxisValue)
    }

    setDomains() {
        const points = this.series.flatMap(line => line.data)
        this.secondarySeries = this.series.filter(line => line.options?.secondary_axis)
        const primaryPoints = this.visibleSeries.filter(line => !line.options?.secondary_axis).flatMap(line => line.data)

        this.xDomain = this.xAxis.domainFor(points, 0)
        this.yDomain = this.yAxis.domainFor(primaryPoints, 1)
        const secondaryPoints = this.visibleSeries.filter(line => line.options?.secondary_axis).flatMap(line => line.data)
        this.secondaryYDomain = [0, d3.max(secondaryPoints, point => point[1]) || 1]
    }

    setMargins() {
        const baseMargin = { top: 20, right: 30, bottom: 20, left: 20 }
        const axisLabelGap = 18
        const axisLabelSize = 14

        this.margin = { ...baseMargin }
        this.setInnerSize()

        const xScale = this.xAxis.scaleFor(this.xDomain, [0, this.innerWidth])
        const yScale = this.yAxis.scaleFor(this.yDomain, [this.innerHeight, 0])
        const xTicks = this.xAxis.ticksFor(xScale)
        const yTicks = this.yAxis.ticksFor(yScale)
        const xTickSize = this.measureTickLabels(xTicks, this.xAxis, xScale)
        const yTickSize = this.measureTickLabels(yTicks, this.yAxis, yScale)
        const secondaryScale = d3.scaleLinear().domain(this.secondaryYDomain).range([this.innerHeight, 0]).nice()
        const secondaryTickSize = this.secondarySeries.length ? this.measureTickLabels(secondaryScale.ticks(this.yAxis.tickCount), this.yAxis, secondaryScale) : { width: 0 }
        const tickSpacing = xTicks.length > 1 ? this.innerWidth / (xTicks.length - 1) : this.innerWidth

        this.rotateXAxisTicks = xTickSize.width > tickSpacing * 0.9

        const xTickHeight = this.rotateXAxisTicks ? this.rotatedHeight(xTickSize) : xTickSize.height
        const xMargin = Math.ceil(xTickHeight + axisLabelGap + axisLabelSize)
        const yMargin = Math.ceil(yTickSize.width + axisLabelGap + axisLabelSize)
        const secondaryYMargin = Math.ceil(secondaryTickSize.width + 10 + axisLabelGap + axisLabelSize)

        this.margin = {
            top: this.xAxis.position === "top" ? xMargin : baseMargin.top,
            right: this.yAxis.position === "right" ? yMargin : this.secondarySeries.length ? secondaryYMargin : baseMargin.right,
            bottom: this.xAxis.position === "bottom" ? xMargin : baseMargin.bottom,
            left: this.yAxis.position === "left" ? yMargin : baseMargin.left
        }

        this.setInnerSize()
    }

    setInnerSize() {
        this.innerWidth = this.chartSize.width - this.margin.left - this.margin.right
        this.innerHeight = this.chartSize.height - this.margin.top - this.margin.bottom
    }

    setScales() {
        this.xScale = this.xAxis.scaleFor(this.xDomain, [0, this.innerWidth])
        this.yScale = this.yAxis.scaleFor(this.yDomain, [this.innerHeight, 0])
        this.secondaryYScale = this.secondarySeries.length ? d3.scaleLinear().domain(this.secondaryYDomain).range([this.innerHeight, 0]).nice() : null
        this.xTickValues = this.xAxis.ticksFor(this.xScale)
        this.yTickValues = this.yAxis.ticksFor(this.yScale)
    }

    drawGrids() {
        if (this.xAxis.grid) this.drawXGrid()
        if (this.yAxis.grid) this.drawYGrid()
    }

    drawXAxis() {
        const axis = this.xAxis.position === "top" ? d3.axisTop(this.xScale) : d3.axisBottom(this.xScale)
        this.applyAxisOptions(axis, this.xAxis, this.xTickValues)

        const axisG = d3.select(this.chartTarget)
            .append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(${this.margin.left},${this.layersAboveSize + this.xAxisPosition()})`)
            .call(axis.tickSizeInner(0).tickSizeOuter(0).tickPadding(10))
            .attr("color", "grey")

        if (this.rotateXAxisTicks) this.rotateXAxisLabels(axisG)
    }

    drawYAxis() {
        const axis = this.yAxis.position === "right" ? d3.axisRight(this.yScale) : d3.axisLeft(this.yScale)
        this.applyAxisOptions(axis, this.yAxis, this.yTickValues)

        d3.select(this.chartTarget)
            .append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${this.yAxisPosition()},${this.layersAboveSize + this.margin.top})`)
            .call(axis.tickSizeInner(0).tickSizeOuter(0).tickPadding(10))
            .attr("color", "grey")
    }

    drawSecondaryYAxis() {
        const labelX = this.chartSize.width - 7
        const labelY = this.layersAboveSize + this.margin.top + this.innerHeight / 2

        d3.select(this.chartTarget)
            .append("g")
            .attr("class", "secondary-y-axis")
            .attr("transform", `translate(${this.margin.left + this.innerWidth},${this.layersAboveSize + this.margin.top})`)
            .call(d3.axisRight(this.secondaryYScale).ticks(this.yAxis.tickCount).tickSizeInner(0).tickSizeOuter(0).tickPadding(10))
            .attr("color", "grey")

        d3.select(this.chartTarget)
            .append("text")
            .attr("class", "secondary-y-axis-label")
            .attr("x", labelX)
            .attr("y", labelY)
            .attr("text-anchor", "middle")
            .attr("transform", `rotate(-90 ${labelX} ${labelY})`)
            .text(this.secondarySeries[0].options.label)
    }

    applyAxisOptions(axis, axisConfig, tickValues) {
        axis.tickValues(tickValues)
        if (axisConfig.tickFormat) axis.tickFormat(axisConfig.axisFormatter(this.scaleFor(axisConfig)))
    }

    scaleFor(axisConfig) {
        return axisConfig.dimension === "x" ? this.xScale : this.yScale
    }

    drawXGrid() {
        const axis = d3.axisBottom(this.xScale)
            .tickValues(this.gridTickValues(this.xTickValues, this.xScale, this.innerWidth))
            .tickSize(-this.innerHeight)
            .tickFormat("")

        const gridG = d3.select(this.chartTarget)
            .append("g")
            .attr("class", "x-grid")
            .attr("transform", `translate(${this.margin.left},${this.layersAboveSize + this.xAxisPosition()})`)
            .call(axis)
            .attr("color", "rgba(0, 0, 0, 0.15)")

        gridG.select(".domain").remove()
    }

    drawYGrid() {
        const axis = d3.axisLeft(this.yScale)
            .tickValues(this.gridTickValues(this.yTickValues, this.yScale, this.innerHeight))
            .tickSize(-this.innerWidth)
            .tickFormat("")

        const gridG = d3.select(this.chartTarget)
            .append("g")
            .attr("class", "y-grid")
            .attr("transform", `translate(${this.margin.left},${this.layersAboveSize + this.margin.top})`)
            .call(axis)
            .attr("color", "rgba(0, 0, 0, 0.15)")

        gridG.select(".domain").remove()
    }

    gridTickValues(tickValues, scale, length) {
        return tickValues.filter(tick => {
            const pixel = this.pixelForTick(tick, scale)
            return pixel > 0 && pixel < length
        })
    }

    pixelForTick(tick, scale) {
        return scale.bandwidth ? scale(tick) + (scale.bandwidth() / 2) : scale(tick)
    }

    drawAxisLabels() {
        this.drawXAxisLabel()
        this.drawYAxisLabel()
    }

    drawXAxisLabel() {
        d3.select(this.chartTarget)
            .append("text")
            .attr("class", "x-axis-label")
            .attr("x", this.margin.left + this.innerWidth / 2)
            .attr("y", this.layersAboveSize + (this.xAxis.position === "top" ? 14 : this.chartSize.height - 7))
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .text(this.xAxis.label)
    }

    drawYAxisLabel() {
        const x = this.yAxis.position === "right" ? this.chartSize.width - 7 : 7
        const y = this.layersAboveSize + this.margin.top + this.innerHeight / 2

        d3.select(this.chartTarget)
            .append("text")
            .attr("class", "y-axis-label")
            .attr("x", x)
            .attr("y", y)
            .attr("text-anchor", "middle")
            .attr("dominant-baseline", "middle")
            .attr("transform", `rotate(-90 ${x} ${y})`)
            .text(this.yAxis.label)
    }

    xAxisPosition() {
        return this.xAxis.position === "top" ? this.margin.top : this.chartSize.height - this.margin.bottom
    }

    yAxisPosition() {
        return this.yAxis.position === "right" ? this.chartSize.width - this.margin.right : this.margin.left
    }

    rotateXAxisLabels(axisG) {
        axisG
            .selectAll(".tick text")
            .attr("text-anchor", this.xAxis.position === "top" ? "start" : "end")
            .attr("dx", this.xAxis.position === "top" ? "0.5em" : "-0.5em")
            .attr("dy", this.xAxis.position === "top" ? "-0.35em" : "0.35em")
            .attr("transform", "rotate(-45)")
    }

    measureTickLabels(ticks, axisConfig, scale) {
        const measureG = d3.select(this.chartTarget)
            .append("g")
            .attr("visibility", "hidden")
            .attr("aria-hidden", "true")

        const sizes = ticks.map(tick => {
            const box = measureG
                .append("text")
                .text(this.formatTick(tick, axisConfig, scale))
                .node()
                .getBBox()

            return { width: box.width, height: box.height }
        })

        measureG.remove()

        return {
            width: d3.max(sizes, size => size.width) || 0,
            height: d3.max(sizes, size => size.height) || 0
        }
    }

    formatTick(tick, axisConfig, scale) {
        return this.axisFormatter(axisConfig, scale)(tick)
    }

    axisFormatter(axisConfig, scale) {
        return axisConfig.axisFormatter(scale)
    }

    valueFormatter(axisConfig, scale) {
        return axisConfig.valueFormatter(scale)
    }

    rotatedHeight(size) {
        const radians = Math.PI / 4
        return (size.width * Math.sin(radians)) + (size.height * Math.cos(radians))
    }
}
